import { useConfirmModal } from 'context/confirmModal';
import { ConfirmModalContent } from 'context/confirmModal/types';

export type NonOwnedSequenceAction =
  | 'add'
  | 'stop'
  | 'resume'
  | 'restart'
  | 'edit'
  | 'status'
  | 'remove';

const NON_OWNED_SEQUENCE_ACTION_COPY: Record<
  NonOwnedSequenceAction,
  {
    title: string;
    text: string;
    confirmText: string;
    type?: ConfirmModalContent['type'];
  }
> = {
  add: {
    title: 'Add contacts owned by other users to a sequence?',
    text: 'These contacts are owned by other users. Adding them to a sequence may affect their tasks and activity.',
    confirmText: 'Add',
  },
  stop: {
    title: 'Stop sequence for contacts owned by other users?',
    text: 'This will stop the sequence and cancel all remaining steps for these contacts',
    confirmText: 'Stop',
  },
  resume: {
    title: 'Resume sequence for contacts owned by other users?',
    text: "This will resume the remaining sequence steps and may affect the owners' tasks and activity.",
    confirmText: 'Resume',
  },
  restart: {
    title: 'Restart sequence for contacts owned by other users?',
    text: "This will restart the sequence from its first step and lose the owners' task personalization.",
    confirmText: 'Restart',
  },
  edit: {
    title: 'Edit assigned task?',
    text: 'This task is assigned to another user. Confirm that you want to apply your changes.',
    confirmText: 'Edit',
  },
  status: {
    title: 'Change the status of sequences owned by other users?',
    text: "These sequences are owned by other users. Changing their status may affect the owners' tasks and activity.",
    confirmText: 'Confirm',
  },
  remove: {
    title: 'Remove contacts from sequences owned by other users?',
    text: "You're about to remove all data of these contacts related to this sequence. This includes all activity data and completed tasks.",
    confirmText: 'Remove',
    type: 'danger',
  },
};

const useNonOwnedSequenceActionGuard = () => {
  const { openModal } = useConfirmModal();

  const openNonOwnedSequenceActionModal = (
    action: NonOwnedSequenceAction,
    onConfirm: () => Promise<void> | void,
  ) => {
    const { type, ...copy } = NON_OWNED_SEQUENCE_ACTION_COPY[action];

    openModal({
      ...copy,
      type: type ?? 'info',
      cancelText: 'Cancel',
      confirmFunction: async () => {
        //The mutations surface their own errors through the global handler, so
        //this only stops a rejection escaping the modal callback unhandled
        try {
          await onConfirm();
        } catch {
          //already reported by the mutation
        }
      },
    });
  };

  return { openNonOwnedSequenceActionModal };
};

export default useNonOwnedSequenceActionGuard;
